import { useState, useEffect, useRef, useContext } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../WebSocketContext';

export default function MeetingAlert() {
  const Navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const { sendMessage } = useWebSocket();

  useEffect(() => {
    // Detect if device is mobile
    const checkIsMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             window.innerWidth <= 768;
    };
    
    setIsMobile(checkIsMobile());
    
    // Listen for resize events to update mobile status
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    
    window.addEventListener('resize', handleResize);
    
    // Only request notification permission on desktop
    if (!checkIsMobile() && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }

    const handleMessage = (event) => {
      const data = event.detail;

      if (data.type === 'meetingAlert' && data.meeting) {
        setMeeting(data.meeting);
        setShow(true);

        if (!checkIsMobile() && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('Meeting Alert', {
            body: `Meeting "${data.meeting.Name}" is starting soon`,
            icon: '/favicon.ico',
            tag: `meeting-${data.meeting.ID || Date.now()}`
          });
        }
      }

      if (data.type === 'activityUpdate' && data.updateType === 'meeting' && data.meetings) {
        setMeetings(data.meetings);
      }

      if (data.meetings) {
        setMeetings(data.meetings);

        const now = new Date();
        const upcomingMeeting = data.meetings.find(meeting => {
          if (!meeting.Date || !meeting.Time) return false;

          try {
            const meetingDate = new Date(meeting.Date);
            const dateString = meetingDate.toISOString().split('T')[0];
            const meetingDateTime = new Date(`${dateString} ${meeting.Time}`);
            const timeDiff = (meetingDateTime - now) / (1000 * 60);
            return timeDiff >= 0 && timeDiff <= 10;
          } catch (error) {
            console.error('Error parsing meeting time:', error);
            return false;
          }
        });

        if (upcomingMeeting) {
          setMeeting(upcomingMeeting);
          setShow(true);

          if (!checkIsMobile() && 'Notification' in window && Notification.permission === 'granted') {
            new Notification('Meeting Starting Soon!', {
              body: `${upcomingMeeting.Name || 'Unnamed Meeting'} is starting soon`,
              icon: '/favicon.ico',
              tag: `meeting-${upcomingMeeting.ID || Date.now()}`
            });
          }
        }
      }
    };

    window.addEventListener('insightos-websocket-message', handleMessage);
    sendMessage({ type: 'requestActivityLog' });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('insightos-websocket-message', handleMessage);
    };
  }, []);

  const handleJoinMeeting = async () => {
    console.log('Navigating to meeting:', meeting);
    
    if (meeting?.Location === 'Google Meet') {
      try {
        // Mark as started in backend
        await fetch(`https://api.insightos.co.uk/api/googlemeetstart/${meeting.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        // Open Google Meet URL
        if (meeting.GoogleMeetURL) {
          window.open(meeting.GoogleMeetURL, '_blank');
        }
      } catch (error) {
        console.error('Error starting Google Meet:', error);
      }
    }

    Navigate('/meeting', { state: { meeting } });
    setShow(false);
  };

  const handleDismiss = () => {
    console.log('Dismissing meeting alert');
    setShow(false);
  };

  return (
    <>
      {/* Meeting Alert Notification */}
      {meeting && show && (
        <>
          {/* Mobile backdrop */}
          {isMobile && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={handleDismiss}
            />
          )}
          <div
            aria-live="assertive"
            className={`pointer-events-none fixed inset-0 z-50 ${
              isMobile 
                ? 'flex items-center justify-center px-4' 
                : 'flex items-end px-4 py-6 sm:items-start sm:p-6'
            }`}
          >
          <div className={`flex w-full flex-col ${
            isMobile 
              ? 'items-center' 
              : 'items-center space-y-4 sm:items-end'
          }`}>
            <Transition 
              show={show}
              enter="transform ease-out duration-300 transition"
              enterFrom={isMobile ? "scale-95 opacity-0" : "translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"}
              enterTo={isMobile ? "scale-100 opacity-100" : "translate-y-0 opacity-100 sm:translate-x-0"}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className={`pointer-events-auto overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5 border-l-4 ${meeting?.Location === 'Google Meet' ? 'border-[#1a73e8]' : 'border-orange-500'} ${
                isMobile 
                  ? 'w-full max-w-sm mx-4' 
                  : 'w-full max-w-md'
              }`}>
                <div className="p-4">
                  {/* Alert Header */}
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0">
                      {meeting?.Location === 'Google Meet' ? (
                        <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Google Meet" className="h-8 w-8" />
                      ) : (
                        <ExclamationTriangleIcon className="h-8 w-8 text-orange-500 animate-pulse" />
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <p className={`text-sm font-bold ${meeting?.Location === 'Google Meet' ? 'text-[#1a73e8]' : 'text-orange-600'}`}>
                        {meeting?.Location === 'Google Meet' ? 'Google Meet Starting!' : 'Meeting Starting Soon!'}
                      </p>
                      <p className="text-xs text-gray-500">
                         Meeting starts soon. Please join on time.
                      </p>
                    </div>
                  </div>
                  
                  {/* Meeting Details */}
                  <div className="space-y-3 mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                      <span className="mr-2">📅</span>
                      {meeting.Name || 'Unnamed Meeting'}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <span className="mr-1">🕐</span>
                        {meeting.Time}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <span className="mr-1">📅</span>
                        {meeting.Date ? new Date(meeting.Date).toLocaleDateString() : 'Today'}
                      </span>
                    </div>
                    
                    {meeting.Location && (
                      <p className="text-sm text-gray-600 flex items-center">
                        <span className="mr-2">📍</span>
                        {meeting.Location}
                      </p>
                    )}
                    
                    {meeting.Attendees && (
                      <p className="text-xs text-gray-500 flex items-center">
                        <span className="mr-2">👥</span>
                        {meeting.Attendees}
                      </p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'space-x-3'}`}>
                    <button
                      type="button"
                      data-time-saved="10"
                      data-component="meeting-alert"  
                      data-feature="join_meeting_click"
                      onClick={handleJoinMeeting}
                      className={`${isMobile ? 'w-full' : 'flex-1'} ${meeting?.Location === 'Google Meet' ? 'bg-[#1a73e8] hover:bg-[#1557b0] focus:ring-[#1a73e8]' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'} active:bg-opacity-80 focus:outline-none focus:ring-2 text-white text-sm font-medium py-3 px-4 rounded-lg transition-colors duration-200 ${
                        isMobile ? 'touch-manipulation' : ''
                      }`}
                    >
                      {meeting?.Location === 'Google Meet' ? 'Join Google Meet' : 'Join Meeting'}
                    </button>
                    <button
                      type="button"
                      onClick={handleDismiss}
                      className={`${isMobile ? 'w-full' : 'flex-1'} bg-gray-200 hover:bg-gray-300 active:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 text-gray-800 text-sm font-medium py-3 px-4 rounded-lg transition-colors duration-200 ${
                        isMobile ? 'touch-manipulation' : ''
                      }`}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>
        </>
      )}
    </>
  );
}
